import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Loader2, BarChart3 } from 'lucide-react';
import { atlasApi, AtlasUser, DiscTime, DiscFator } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  onAbrir: (funccodi: number) => void;
}

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

interface Depto { codigo: number | string; nome: string; }

const StatCard: React.FC<{ titulo: string; valor: React.ReactNode; cor?: string }> = ({ titulo, valor, cor }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4">
    <div className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">{titulo}</div>
    <div className={`text-2xl font-bold mt-1 ${cor || 'text-gray-900'}`}>{valor}</div>
  </div>
);

const Comparativo: React.FC<Props> = ({ user, onAbrir }) => {
  const [departamentos, setDepartamentos] = useState<Depto[]>([]);
  const [departamento, setDepartamento] = useState<string>('');
  const [dados, setDados] = useState<DiscTime | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    atlasApi.organograma(user)
      .then(r => {
        if (cancel) return;
        const deptos: Depto[] = (r.grupos || [])
          .filter((g: any) => g.centro_custo_codigo != null)
          .map((g: any) => ({ codigo: g.centro_custo_codigo, nome: g.centro_custo_nome }));
        setDepartamentos(deptos);
      })
      .catch(() => { /* dropdown opcional */ });
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.discTime(user, departamento || undefined)
      .then(r => { if (!cancel) setDados(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar comparativo'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, departamento]);

  const cobertura = dados && dados.total_colaboradores > 0
    ? Math.round((dados.total_respondidos / dados.total_colaboradores) * 100)
    : 0;

  const barData = dados ? FATORES.map(f => ({ fator: f, total: dados.distribuicao[f] || 0 })) : [];

  return (
    <div className="space-y-4 max-w-5xl">
      {/* Filtro */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" /> Comparativo do time
        </h2>
        <select
          value={departamento}
          onChange={e => setDepartamento(e.target.value)}
          className="ml-auto px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <option value="">Todos os departamentos</option>
          {departamentos.map(d => (
            <option key={String(d.codigo)} value={String(d.codigo)}>{d.nome}</option>
          ))}
        </select>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...
        </div>
      ) : dados ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard titulo="Respondidos" valor={dados.total_respondidos} cor="text-purple-700" />
            <StatCard titulo="Colaboradores" valor={dados.total_colaboradores} />
            <StatCard titulo="Cobertura" valor={`${cobertura}%`} cor="text-emerald-600" />
          </div>

          {/* Distribuição */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Distribuição de estilos</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData}>
                <XAxis dataKey="fator" tick={{ fontSize: 13, fontWeight: 700 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                  {barData.map(d => (
                    <Cell key={d.fator} fill={DISC_CORES[d.fator as DiscFator]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabela por pessoa */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {dados.por_pessoa.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">
                Ninguém respondeu o DISC neste filtro ainda.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Colaborador</th>
                    <th className="px-4 py-2 text-left">Cargo</th>
                    <th className="px-4 py-2 text-left">Estilo principal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dados.por_pessoa.map(p => (
                    <tr
                      key={p.funccodi}
                      onClick={() => onAbrir(p.funccodi)}
                      className="hover:bg-purple-50/40 cursor-pointer transition"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <AvatarColaborador nome={p.nome} fotoUrl={p.foto_url} size={32} />
                          <span className="font-medium text-gray-900">{p.nome || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.cargo || '—'}</td>
                      <td className="px-4 py-3">
                        {p.estilo_principal ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: DISC_CORES[p.estilo_principal] }}
                          >
                            {p.estilo_principal} · {DISC_LABEL[p.estilo_principal]}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default Comparativo;
